import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import type {
  Contact,
  ContactOwnerId,
  ContactStage,
  MarketingEmailStatus,
} from '@otw/models/leads/contacts'
import {
  MARKETING_EMAIL_STATUS_LABEL,
  MARKETING_EMAIL_STATUSES,
  normalizeContactBirthday,
  resolveContactMarketingEmailStatus,
  resolveContactMarketingEmailSuppressed,
} from '@otw/models/leads/contacts'
import type { ContactCompany, ContactCompanyRole } from '@otw/models/leads/contactCompanies'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useContactCompanies } from 'hooks/contacts/useContactCompanies'
import {
  findUserByEmail,
  getUserDisplayName,
  useInventoryAssignableUsers,
  useSalesAssignableUsers,
  useUsers,
} from 'hooks/useUsers'
import {
  CreateContactInput,
  UpdateContactInput,
  useContacts,
  contactsQueryKey,
} from 'hooks/contacts/useContacts'
import ContactDriversLicenseSection from '../ContactDriversLicenseSection'
import { fileToBase64 } from '@/core/utils/fileUtils'
import { getDriverLicenseUploadContentType } from '@/components/DriversLicensePreview'
import { apiClient } from '@/core/api/apiClient'
import { applyContactCompanyMemberships } from '../../util/applyContactCompanyMemberships'
import {
  diffContactCompanyMembershipSelections,
  getContactMemberships,
} from '../../util/contactCompanyMembership'
import {
  CONTACT_EMAIL_PATTERN,
  contactMatchesEmail,
  normalizeContactEmail,
  normalizeContactName,
  normalizeContactPhoneDigits,
} from '../../util/contactIdentity'

interface SelectedCompanyMembership {
  company: ContactCompany
  role: ContactCompanyRole
}

interface ContactFormDialogProps {
  open: boolean
  onClose: () => void
  /** When provided, the dialog operates in edit mode for this contact. */
  contact?: Contact | null
  /** After create, adds the new contact as a member of this company. */
  linkToCompany?: { companyId: string; role?: ContactCompanyRole }
}

const STAGE_OPTIONS: { value: ContactStage; label: string }[] = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'customer', label: 'Customer' },
  { value: 'unqualified', label: 'Unqualified' },
]

const formatDigitsAsUsPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const repSelectValue = (id: ContactOwnerId): string =>
  id === null || id === undefined ? '' : String(id)

const parseRepSelect = (v: string): ContactOwnerId => {
  if (v === '') return null
  const n = Number(v)
  return Number.isFinite(n) && String(n) === v ? n : v
}

const ROLE_OPTIONS: { value: ContactCompanyRole; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'manager', label: 'Manager' },
  { value: 'partner', label: 'Partner' },
]

const defaultRoleForCompany = (
  companyId: string,
  linkToCompany?: { companyId: string; role?: ContactCompanyRole },
): ContactCompanyRole => {
  if (linkToCompany?.companyId === companyId) {
    return linkToCompany.role ?? 'manager'
  }
  return 'manager'
}

const ContactFormDialog: React.FC<ContactFormDialogProps> = ({
  open,
  onClose,
  contact,
  linkToCompany,
}) => {
  const theme = useTheme()
  const { createContact, updateContact, isSaving, contacts } = useContacts()
  const queryClient = useQueryClient()
  const {
    companies: contactCompanies,
    addCompanyMember,
    removeCompanyMember,
    updateCompanyMemberRole,
  } = useContactCompanies()
  const { users: assignableOwners, isLoading: isLoadingOwners } = useSalesAssignableUsers()
  const {
    users: inventoryAssignableOwners,
    isLoading: isLoadingInventoryOwners,
  } = useInventoryAssignableUsers()
  const { users: allUsers } = useUsers()

  const sortedCompanyOptions = useMemo(
    () =>
      [...contactCompanies].sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      ),
    [contactCompanies],
  )

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [secondaryEmail, setSecondaryEmail] = useState('')
  const [selectedCompanyMemberships, setSelectedCompanyMemberships] = useState<
    SelectedCompanyMembership[]
  >([])
  const [ownerId, setOwnerId] = useState<ContactOwnerId>(null)
  const [inventoryOwnerId, setInventoryOwnerId] = useState<ContactOwnerId>(null)
  const [stage, setStage] = useState<ContactStage>('prospect')
  const [inventoryStage, setInventoryStage] = useState<ContactStage>('prospect')
  const [marketingEmailStatus, setMarketingEmailStatus] =
    useState<MarketingEmailStatus>('subscribed')
  const [marketingEmailSuppressed, setMarketingEmailSuppressed] = useState(false)
  const [birthday, setBirthday] = useState<Dayjs | null>(null)
  const [pendingLicenseFile, setPendingLicenseFile] = useState<File | null>(null)
  const [isUploadingLicense, setIsUploadingLicense] = useState(false)
  const [isSyncingMemberships, setIsSyncingMemberships] = useState(false)
  const [duplicateWarningOpen, setDuplicateWarningOpen] = useState(false)
  const prevOpenRef = useRef(false)
  const stageAtOpenRef = useRef<ContactStage>('prospect')
  const inventoryStageAtOpenRef = useRef<ContactStage>('prospect')

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current
    prevOpenRef.current = open
    if (!justOpened) return

    setDuplicateWarningOpen(false)
    if (contact) {
      setName(contact.name ?? '')
      setPhone(contact.phone ?? '')
      setEmail(contact.email ?? '')
      setSecondaryEmail(contact.secondaryEmail ?? '')
      setSelectedCompanyMemberships(
        getContactMemberships(contact.id, contactCompanies).map((membership) => ({
          company: membership.company,
          role: membership.role,
        })),
      )
      setOwnerId(contact.ownerId ?? null)
      setInventoryOwnerId(contact.inventoryOwnerId ?? null)
      const salesStage = contact.stage ?? 'prospect'
      const buyingStage = contact.inventoryStage ?? 'prospect'
      setStage(salesStage)
      setInventoryStage(buyingStage)
      setMarketingEmailStatus(resolveContactMarketingEmailStatus(contact))
      setMarketingEmailSuppressed(resolveContactMarketingEmailSuppressed(contact))
      stageAtOpenRef.current = salesStage
      inventoryStageAtOpenRef.current = buyingStage
      setBirthday(
        contact.birthday && dayjs(contact.birthday).isValid()
          ? dayjs(contact.birthday)
          : null,
      )
      setPendingLicenseFile(null)
    } else {
      setName('')
      setPhone('')
      setEmail('')
      setSecondaryEmail('')
      const linkedCompany = linkToCompany?.companyId
        ? sortedCompanyOptions.find((company) => company.id === linkToCompany.companyId) ?? null
        : null
      setSelectedCompanyMemberships(
        linkedCompany
          ? [
              {
                company: linkedCompany,
                role: defaultRoleForCompany(linkedCompany.id, linkToCompany),
              },
            ]
          : [],
      )
      setOwnerId(null)
      setInventoryOwnerId(null)
      setStage('prospect')
      setInventoryStage('prospect')
      setMarketingEmailStatus('subscribed')
      setMarketingEmailSuppressed(false)
      stageAtOpenRef.current = 'prospect'
      inventoryStageAtOpenRef.current = 'prospect'
      setBirthday(null)
      setPendingLicenseFile(null)
    }
  }, [open, contact, linkToCompany, contactCompanies, sortedCompanyOptions])

  const selectedCompanies = useMemo(
    () => selectedCompanyMemberships.map((membership) => membership.company),
    [selectedCompanyMemberships],
  )

  const handleCompaniesChange = (_event: React.SyntheticEvent, value: ContactCompany[]) => {
    setSelectedCompanyMemberships((prev) => {
      const roleByCompanyId = new Map(prev.map((entry) => [entry.company.id, entry.role]))
      return value.map((company) => ({
        company,
        role:
          roleByCompanyId.get(company.id) ??
          defaultRoleForCompany(company.id, linkToCompany),
      }))
    })
  }

  const handleCompanyRoleChange = (companyId: string, role: ContactCompanyRole) => {
    setSelectedCompanyMemberships((prev) =>
      prev.map((entry) =>
        entry.company.id === companyId ? { ...entry, role } : entry,
      ),
    )
  }

  const emailTrim = email.trim()
  const secondaryEmailTrim = secondaryEmail.trim()
  const emailInvalid = emailTrim.length > 0 && !CONTACT_EMAIL_PATTERN.test(emailTrim)
  const secondaryEmailInvalid =
    secondaryEmailTrim.length > 0 && !CONTACT_EMAIL_PATTERN.test(secondaryEmailTrim)
  const emailsMatchEachOther =
    emailTrim.length > 0 &&
    secondaryEmailTrim.length > 0 &&
    normalizeContactEmail(emailTrim) === normalizeContactEmail(secondaryEmailTrim)
  const valid =
    name.trim().length > 0 &&
    !emailInvalid &&
    !secondaryEmailInvalid &&
    !emailsMatchEachOther
  const isBusy = isSaving || isUploadingLicense || isSyncingMemberships

  const otherContacts = useMemo(
    () => contacts.filter((c) => c.id !== contact?.id),
    [contacts, contact?.id],
  )

  const nameDuplicate = useMemo(() => {
    const normalized = normalizeContactName(name)
    if (!normalized) return null
    return (
      otherContacts.find((c) => normalizeContactName(c.name ?? '') === normalized) ?? null
    )
  }, [name, otherContacts])

  const emailDuplicate = useMemo(() => {
    const normalized = normalizeContactEmail(email)
    if (!normalized || emailInvalid) return null
    return otherContacts.find((c) => contactMatchesEmail(c, normalized)) ?? null
  }, [email, emailInvalid, otherContacts])

  const secondaryEmailDuplicate = useMemo(() => {
    const normalized = normalizeContactEmail(secondaryEmail)
    if (!normalized || secondaryEmailInvalid) return null
    return otherContacts.find((c) => contactMatchesEmail(c, normalized)) ?? null
  }, [secondaryEmail, secondaryEmailInvalid, otherContacts])

  const phoneDuplicate = useMemo(() => {
    const digits = normalizeContactPhoneDigits(phone)
    if (!digits) return null
    return (
      otherContacts.find(
        (c) => c.phone && normalizeContactPhoneDigits(c.phone) === digits,
      ) ?? null
    )
  }, [phone, otherContacts])

  const duplicateReasons = useMemo(() => {
    const reasons: string[] = []
    if (nameDuplicate) reasons.push('name')
    if (emailDuplicate) reasons.push('email')
    if (secondaryEmailDuplicate) reasons.push('secondary email')
    if (phoneDuplicate) reasons.push('phone number')
    return reasons
  }, [nameDuplicate, emailDuplicate, secondaryEmailDuplicate, phoneDuplicate])

  const duplicateContacts = useMemo(() => {
    const byId = new Map<string, Contact>()
    for (const match of [
      nameDuplicate,
      emailDuplicate,
      secondaryEmailDuplicate,
      phoneDuplicate,
    ]) {
      if (match) byId.set(match.id, match)
    }
    return [...byId.values()]
  }, [nameDuplicate, emailDuplicate, secondaryEmailDuplicate, phoneDuplicate])

  const hasDuplicateWarning = duplicateReasons.length > 0

  const dialogTitle = contact ? 'Edit contact' : 'Add contact'
  const submitLabel = useMemo(() => {
    if (isBusy) return 'Saving…'
    return contact ? 'Save' : 'Create'
  }, [isBusy, contact])

  const uploadPendingLicense = async (contactId: string, file: File) => {
    setIsUploadingLicense(true)
    try {
      const base64 = await fileToBase64(file)
      await apiClient.portal.admin.uploadContactDriverLicenseFront(
        base64,
        contactId,
        getDriverLicenseUploadContentType(file),
      )
      await queryClient.invalidateQueries({ queryKey: contactsQueryKey })
    } finally {
      setIsUploadingLicense(false)
    }
  }

  const syncMemberships = async (contactId: string) => {
    const selections = selectedCompanyMemberships.map((membership) => ({
      companyId: membership.company.id,
      role: membership.role,
    }))
    const { toAdd, toRemove, toUpdateRole } = diffContactCompanyMembershipSelections(
      contactId,
      contactCompanies,
      selections,
    )
    if (toAdd.length === 0 && toRemove.length === 0 && toUpdateRole.length === 0) return

    setIsSyncingMemberships(true)
    try {
      await applyContactCompanyMemberships(
        {
          contactId,
          toAdd,
          toRemove,
          toUpdateRole,
        },
        { addCompanyMember, removeCompanyMember, updateCompanyMemberRole },
      )
    } finally {
      setIsSyncingMemberships(false)
    }
  }

  const saveContact = async () => {
    const birthdayValue =
      birthday?.isValid() ? normalizeContactBirthday(birthday.format('YYYY-MM-DD')) ?? '' : ''
    if (contact) {
      const input: UpdateContactInput = {
        id: contact.id,
        name: name.trim(),
        email: email.trim(),
        secondaryEmail: secondaryEmail.trim(),
        phone: phone.trim(),
        birthday: birthdayValue,
        ownerId,
        inventoryOwnerId,
        ...(stage !== stageAtOpenRef.current ? { stage } : {}),
        ...(inventoryStage !== inventoryStageAtOpenRef.current
          ? { inventoryStage }
          : {}),
        marketingEmailStatus,
        marketingEmailSuppressed,
      }
      await updateContact(input)
      await syncMemberships(contact.id)
    } else {
      const trimmedEmail = email.trim()
      const matchedUser = trimmedEmail ? findUserByEmail(allUsers, trimmedEmail) : undefined
      const matchedUserId = matchedUser?.id != null ? String(matchedUser.id).trim() : ''
      const input: CreateContactInput = {
        name: name.trim(),
        email: trimmedEmail,
        secondaryEmail: secondaryEmail.trim(),
        phone: phone.trim(),
        ...(birthdayValue ? { birthday: birthdayValue } : {}),
        ownerId,
        inventoryOwnerId,
        stage,
        inventoryStage,
        marketingEmailStatus,
        marketingEmailSuppressed,
        ...(matchedUserId ? { userId: matchedUserId } : {}),
      }
      const newContactId = await createContact(input)
      if (newContactId) {
        if (pendingLicenseFile) {
          await uploadPendingLicense(newContactId, pendingLicenseFile)
          setPendingLicenseFile(null)
        }
        await syncMemberships(newContactId)
      }
    }
    setDuplicateWarningOpen(false)
    onClose()
  }

  const handleSubmitClick = () => {
    if (hasDuplicateWarning) {
      setDuplicateWarningOpen(true)
      return
    }
    void saveContact()
  }

  const handleConfirmDuplicate = () => {
    void saveContact()
  }

  const handleCancelDuplicate = () => {
    setDuplicateWarningOpen(false)
  }

  const formatDuplicateReasons = (reasons: string[]): string => {
    if (reasons.length <= 1) return reasons[0] ?? ''
    if (reasons.length === 2) return `${reasons[0]} and ${reasons[1]}`
    return `${reasons.slice(0, -1).join(', ')}, and ${reasons[reasons.length - 1]}`
  }

  return (
    <>
    <Dialog open={open} onClose={() => !isBusy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <TextField
              label="Name"
              required
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isBusy}
              autoFocus
            />
            {nameDuplicate ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                A contact named "{nameDuplicate.name}" already exists.
              </Alert>
            ) : null}
          </Box>
          <Box>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isBusy}
              placeholder="Optional"
              error={emailInvalid}
              helperText={emailInvalid ? 'Invalid email address' : undefined}
            />
            {emailDuplicate ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {`Email matches existing contact "${emailDuplicate.name}${
                  emailDuplicate.email
                    ? ` (${emailDuplicate.email})`
                    : emailDuplicate.secondaryEmail
                      ? ` (${emailDuplicate.secondaryEmail})`
                      : ''
                }".`}
              </Alert>
            ) : null}
          </Box>
          <Box>
            <TextField
              label="Secondary email"
              type="email"
              fullWidth
              value={secondaryEmail}
              onChange={(e) => setSecondaryEmail(e.target.value)}
              disabled={isBusy}
              placeholder="Optional"
              error={secondaryEmailInvalid || emailsMatchEachOther}
              helperText={
                secondaryEmailInvalid
                  ? 'Invalid email address'
                  : emailsMatchEachOther
                    ? 'Secondary email must differ from primary email'
                    : undefined
              }
            />
            {secondaryEmailDuplicate ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {`Secondary email matches existing contact "${secondaryEmailDuplicate.name}${
                  secondaryEmailDuplicate.email
                    ? ` (${secondaryEmailDuplicate.email})`
                    : secondaryEmailDuplicate.secondaryEmail
                      ? ` (${secondaryEmailDuplicate.secondaryEmail})`
                      : ''
                }".`}
              </Alert>
            ) : null}
          </Box>
          <Box>
            <TextField
              label="Phone"
              fullWidth
              value={phone}
              onChange={(e) => setPhone(formatDigitsAsUsPhone(e.target.value))}
              disabled={isBusy}
              placeholder="Optional"
            />
            {phoneDuplicate ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {`Phone matches existing contact "${phoneDuplicate.name}${phoneDuplicate.phone ? ` (${phoneDuplicate.phone})` : ''}".`}
              </Alert>
            ) : null}
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Birthday"
              value={birthday}
              onChange={(value) => setBirthday(value)}
              disabled={isBusy}
              slotProps={{
                textField: {
                  fullWidth: true,
                  placeholder: 'Optional',
                },
                field: { clearable: true },
              }}
            />
          </LocalizationProvider>

          <Box>
            <Autocomplete
              multiple
              options={sortedCompanyOptions}
              value={selectedCompanies}
              onChange={handleCompaniesChange}
              disabled={isBusy}
              getOptionLabel={(option) => option.name || 'Unnamed company'}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Companies"
                  placeholder={
                    selectedCompanies.length === 0 ? 'Search companies' : ''
                  }
                  helperText="Pick companies below, then set this contact's role at each one."
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack>
                    <Typography variant="body2" fontWeight={600}>
                      {option.name || 'Unnamed company'}
                    </Typography>
                    {option.website ? (
                      <Typography variant="caption" color="text.secondary">
                        {option.website}
                      </Typography>
                    ) : null}
                  </Stack>
                </li>
              )}
            />
            {selectedCompanyMemberships.length > 0 ? (
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {selectedCompanyMemberships.map(({ company, role }) => (
                  <Paper
                    key={company.id}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {company.name || 'Unnamed company'}
                      </Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 130 }} disabled={isBusy}>
                      <InputLabel id={`contact-company-role-${company.id}`}>Role</InputLabel>
                      <Select
                        labelId={`contact-company-role-${company.id}`}
                        label="Role"
                        value={role}
                        onChange={(e) =>
                          handleCompanyRoleChange(company.id, e.target.value as ContactCompanyRole)
                        }
                      >
                        {ROLE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Paper>
                ))}
              </Stack>
            ) : null}
          </Box>

          <FormControl fullWidth>
            <InputLabel id="contact-sales-owner-label">Sales owner</InputLabel>
            <Select
              labelId="contact-sales-owner-label"
              label="Sales owner"
              value={repSelectValue(ownerId)}
              onChange={(e) => setOwnerId(parseRepSelect(String(e.target.value)))}
              disabled={isBusy || isLoadingOwners}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {assignableOwners.map((user) => (
                <MenuItem key={String(user.id)} value={String(user.id)}>
                  {getUserDisplayName(user)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="contact-inventory-owner-label">Inventory owner</InputLabel>
            <Select
              labelId="contact-inventory-owner-label"
              label="Inventory owner"
              value={repSelectValue(inventoryOwnerId)}
              onChange={(e) => setInventoryOwnerId(parseRepSelect(String(e.target.value)))}
              disabled={isBusy || isLoadingInventoryOwners}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {inventoryAssignableOwners.map((user) => (
                <MenuItem key={String(user.id)} value={String(user.id)}>
                  {getUserDisplayName(user)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="contact-sales-stage-label">Sales stage</InputLabel>
            <Select
              labelId="contact-sales-stage-label"
              label="Sales stage"
              value={stage}
              onChange={(e) => setStage(e.target.value as ContactStage)}
              disabled={isBusy}
            >
              {STAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="contact-inventory-stage-label">Inventory stage</InputLabel>
            <Select
              labelId="contact-inventory-stage-label"
              label="Inventory stage"
              value={inventoryStage}
              onChange={(e) => setInventoryStage(e.target.value as ContactStage)}
              disabled={isBusy}
            >
              {STAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="contact-marketing-email-status-label">
              Marketing email status
            </InputLabel>
            <Select
              labelId="contact-marketing-email-status-label"
              label="Marketing email status"
              value={marketingEmailStatus}
              onChange={(e) =>
                setMarketingEmailStatus(e.target.value as MarketingEmailStatus)
              }
              disabled={isBusy}
            >
              {MARKETING_EMAIL_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {MARKETING_EMAIL_STATUS_LABEL[status]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={marketingEmailSuppressed}
                onChange={(event) => setMarketingEmailSuppressed(event.target.checked)}
                disabled={isBusy}
              />
            }
            label="Suppress marketing email"
          />
          <Typography variant="caption" color="text.secondary">
            Suppressed contacts are never eligible for campaigns, even if subscribed.
          </Typography>

          <ContactDriversLicenseSection
            contactId={contact?.id}
            storagePath={contact?.driver_license_front_storage_path}
            pendingFile={pendingLicenseFile}
            onPendingFileChange={setPendingLicenseFile}
            canEdit={!isBusy}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isBusy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitClick}
          disabled={isBusy || !valid}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={duplicateWarningOpen}
      onClose={handleCancelDuplicate}
      maxWidth="sm"
      fullWidth
      aria-labelledby="duplicate-contact-dialog-title"
      aria-describedby="duplicate-contact-dialog-description"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        id="duplicate-contact-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.15)} 0%, ${alpha(theme.palette.warning.main, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
        }}
      >
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: alpha(theme.palette.warning.main, 0.18),
            color: theme.palette.warning.main,
            width: 40,
            height: 40,
          }}
        >
          <WarningAmberIcon />
        </Avatar>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.warning.dark, fontWeight: 700, letterSpacing: 1 }}
          >
            POTENTIAL DUPLICATE
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            Duplicate Contact Warning
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <DialogContentText id="duplicate-contact-dialog-description" sx={{ mb: 2 }}>
          This contact might already exist — the same{' '}
          <strong>{formatDuplicateReasons(duplicateReasons)}</strong> was found on an existing
          contact.
        </DialogContentText>

        {duplicateContacts.map((existing) => (
          <Paper
            key={existing.id}
            variant="outlined"
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.04),
              borderColor: alpha(theme.palette.warning.main, 0.3),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                fontWeight: 700,
                color: 'text.secondary',
              }}
            >
              Existing contact
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Name:</strong> {existing.name}
              </Typography>
              {existing.email ? (
                <Typography variant="body2">
                  <strong>Email:</strong> {existing.email}
                </Typography>
              ) : null}
              {existing.secondaryEmail ? (
                <Typography variant="body2">
                  <strong>Secondary email:</strong> {existing.secondaryEmail}
                </Typography>
              ) : null}
              {existing.phone ? (
                <Typography variant="body2">
                  <strong>Phone:</strong> {existing.phone}
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        ))}

        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
          {contact ? 'Do you want to save this contact anyway?' : 'Do you want to create a new contact anyway?'}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancelDuplicate} disabled={isBusy}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDuplicate}
          variant="contained"
          color="warning"
          autoFocus
          disabled={isBusy}
        >
          {contact ? 'Save anyway' : 'Create anyway'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  )
}

export default ContactFormDialog
